/**
 * 模块: PriceCVRInsight Table Columns
 * 职责: 价格-转化率洞察表格列配置
 * 限制: <280行
 */

import React from 'react';
import { Tag } from 'antd';
import type { ColumnType } from 'antd/es/table';

/**
 * 价格区间表格列
 */
export const priceRangeColumns = (recommendedRange: string): ColumnType<any>[] => [
  {
    title: '价格区间',
    dataIndex: 'price_range',
    key: 'price_range',
    render: (range: string) => {
      const isBest = range === recommendedRange;
      return isBest ? <Tag color="gold">{range} ⭐</Tag> : range;
    },
  },
  {
    title: '关键词数',
    dataIndex: 'keyword_count',
    key: 'keyword_count',
    sorter: (a: any, b: any) => a.keyword_count - b.keyword_count,
  },
  {
    title: '总搜索量',
    dataIndex: 'total_search_volume',
    key: 'total_search_volume',
    render: (val: number) => val.toLocaleString(),
    sorter: (a: any, b: any) => a.total_search_volume - b.total_search_volume,
  },
  {
    title: '平均市场转化率',
    dataIndex: 'avg_market_cvr',
    key: 'avg_market_cvr',
    render: (val: number) => {
      const color = val >= 20 ? 'green' : val >= 10 ? 'orange' : 'default';
      return <Tag color={color}>{val.toFixed(2)}%</Tag>;
    },
    sorter: (a: any, b: any) => a.avg_market_cvr - b.avg_market_cvr,
  },
];

/**
 * 价格劣势关键词表格列
 */
export const disadvantageColumns: ColumnType<any>[] = [
  {
    title: '搜索词',
    dataIndex: 'search_query',
    key: 'search_query',
    width: 200,
  },
  {
    title: '搜索量',
    dataIndex: 'search_volume',
    key: 'search_volume',
    width: 100,
    render: (val: number) => val > 0 ? val.toLocaleString() : '-',
    sorter: (a: any, b: any) => (a.search_volume || 0) - (b.search_volume || 0),
  },
  {
    title: 'ASIN价格',
    dataIndex: 'asin_price',
    key: 'asin_price',
    render: (val: number) => `$${val.toFixed(2)}`,
  },
  {
    title: '市场价格',
    dataIndex: 'market_price',
    key: 'market_price',
    render: (val: number) => `$${val.toFixed(2)}`,
  },
  {
    title: 'ASIN转化率',
    dataIndex: 'asin_cvr',
    key: 'asin_cvr',
    render: (val: number) => `${val.toFixed(2)}%`,
  },
  {
    title: '市场转化率',
    dataIndex: 'market_cvr',
    key: 'market_cvr',
    render: (val: number) => `${val.toFixed(2)}%`,
  },
  {
    title: '转化率差',
    dataIndex: 'cvr_diff_rate',
    key: 'cvr_diff_rate',
    render: (val: number) => {
      const color = val < -30 ? 'red' : val < -15 ? 'orange' : 'default';
      return <Tag color={color}>{val.toFixed(1)}%</Tag>;
    },
    sorter: (a: any, b: any) => a.cvr_diff_rate - b.cvr_diff_rate,
  },
  {
    title: '优化机会',
    dataIndex: 'opportunity',
    key: 'opportunity',
    render: (text: string) => {
      if (!text) return <Tag>-</Tag>;
      if (text.includes('高')) return <Tag color="red">高</Tag>;
      if (text.includes('中')) return <Tag color="orange">中</Tag>;
      return <Tag>低</Tag>;
    },
  },
];

/**
 * 价格优势关键词表格列
 */
export const advantageColumns: ColumnType<any>[] = [
  {
    title: '搜索词',
    dataIndex: 'search_query',
    key: 'search_query',
    width: 200,
  },
  {
    title: '搜索量',
    dataIndex: 'search_volume',
    key: 'search_volume',
    width: 100,
    render: (val: number) => val > 0 ? val.toLocaleString() : '-',
    sorter: (a: any, b: any) => (a.search_volume || 0) - (b.search_volume || 0),
  },
  {
    title: 'ASIN价格',
    dataIndex: 'asin_price',
    key: 'asin_price',
    render: (val: number) => `$${val.toFixed(2)}`,
  },
  {
    title: '市场价格',
    dataIndex: 'market_price',
    key: 'market_price',
    render: (val: number) => `$${val.toFixed(2)}`,
  },
  {
    title: '转化率优势',
    dataIndex: 'cvr_diff_rate',
    key: 'cvr_diff_rate',
    render: (val: number) => <Tag color="green">+{val.toFixed(1)}%</Tag>,
    sorter: (a: any, b: any) => b.cvr_diff_rate - a.cvr_diff_rate,
  },
];

